import {
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { EntitySettingsView } from '@/components/entity-settings/entity-settings-view';
import { FeatureGuard } from '@/components/feature-guard';
import { useCodexAccessLayer } from '@/features/codex';
import { useNovelRepository } from '@/hooks/useNovelRepository';

export const Route = createFileRoute('/codex/settings')({
  component: CodexSettingsPage,
});

function CodexSettingsContent() {
  const { t } = useTranslation();
  const { novel } = useNovelRepository();
  const { entries } = useCodexAccessLayer();
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const categories = useMemo(() => {
    const categoryMap = novel?.codexCategories || {};
    const parents = novel?.entitiesMeta?.codexCategoryParents || novel?.codexCategoryParents || {};
    return Object.keys(categoryMap).sort((a, b) => {
      const label = (id: string) => `${parents[id] ? `${categoryMap[parents[id]] || parents[id]}/` : ''}${categoryMap[id] || id}`;
      return label(a).localeCompare(label(b), undefined, { sensitivity: 'base' });
    });
  }, [novel]);
  const categoryLabel = (categoryId: string) => {
    const name = novel?.codexCategories?.[categoryId] || categoryId;
    const parentId = novel?.entitiesMeta?.codexCategoryParents?.[categoryId] || novel?.codexCategoryParents?.[categoryId];
    return parentId ? `${novel?.codexCategories?.[parentId] || parentId}/${name}` : name;
  };

  // If there's only one category, select it by default
  useMemo(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0]);
    }
  }, [categories, selectedCategory]);

  if (categories.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {t('entity_list.codex.tabs.no_categories')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ px: 4, pt: 4, bgcolor: 'background.default' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('entity_list.codex.tabs.configuring_fields')}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              displayEmpty
              sx={{ bgcolor: 'background.paper' }}
            >
              {categories.map(categoryId => (
                <MenuItem key={categoryId} value={categoryId}>
                  {categoryLabel(categoryId)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {selectedCategory && (
        <EntitySettingsView
          key={selectedCategory}
          type={`codex:${selectedCategory}`}
          title={t('entity_list.codex.tabs.settings_title', { category: categoryLabel(selectedCategory) })}
          breadcrumbLabel={t('entity_list.codex.title')}
          breadcrumbLink="/codex"
        />
      )}
    </Box>
  );
}

function CodexSettingsPage() {
  return (
    <FeatureGuard featureKey="should_display_codex_page">
      <AuthGuard>
        <Suspense fallback={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
            <CircularProgress size={32} />
          </Box>
        }>
          <CodexSettingsContent />
        </Suspense>
      </AuthGuard>
    </FeatureGuard>
  );
}
